// src/components/forms/DynamicForm.jsx

/**
 * DynamicForm.jsx
 * Componente para renderizar un formulario dinámico.
 * Soporta validaciones, campos condicionales y modo readonly.
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import UbicacionSelector from "../UbicacionSelector";

function DynamicForm({
  fields = [],
  recordData = {},
  view = "activar",
  onChange,
  onSubmit,
  readOnly = false,
  submitButtonText, // 🆕 NUEVO: Texto personalizado del botón
  submitButtonIcon, // 🆕 NUEVO: Icono personalizado del botón
  submitButtonVariant = "primary", // 🆕 NUEVO: Variante del botón
}) {
  const [formState, setFormState] = useState({ ...recordData });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormState({ ...recordData });
    setErrors({});
  }, [recordData, view]);

  const handleChange = (fieldKey, val) => {
    if (readOnly) return;

    setFormState((prev) => {
      const prevVal = prev[fieldKey];
      const same =
        (Array.isArray(prevVal) &&
          Array.isArray(val) &&
          prevVal.length === val.length &&
          prevVal.every((v, i) => v === val[i])) ||
        prevVal === val;
      if (same) return prev;

      const newData = { ...prev, [fieldKey]: val };
      onChange?.(fieldKey, val);
      return newData;
    });

    // Limpiar error del campo actual y relacionados
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      
      if (fieldKey === 'correoPropietario') {
        delete newErrors['correoConfirmacion'];
      }
      
      return newErrors;
    });
  };

  const isValidEmail = (str) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(str);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    const newErrors = {};
    fields.forEach((f) => {
      if (!f.validation) return;
      const validations = Array.isArray(f.validation) ? f.validation : [f.validation];

      let val = formState[f.fieldKey] ?? "";
      if (Array.isArray(val)) {
        val = val.length ? "selected" : "";
      } else {
        val = val?.trim?.() ?? val;
      }

      validations.forEach((rule) => {
        if (rule === "required" && (val === "" || val === null || val === undefined)) {
          newErrors[f.fieldKey] = "Este campo es requerido.";
        }
        
        if (rule === "email" && val && !isValidEmail(val)) {
          newErrors[f.fieldKey] = "Ingresa un correo válido.";
        }
        
        if (rule === "match_correo" && f.fieldKey === "correoConfirmacion") {
          const correoPrincipal = formState.correoPropietario?.trim() || "";
          const correoConfirm = val;
          
          if (correoConfirm && correoPrincipal !== correoConfirm) {
            newErrors[f.fieldKey] = "Los correos no coinciden. Verifica e intenta de nuevo.";
          }
        }
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      
      return;
    }

    setIsSubmitting(true);
    try {
      const finalForm = { ...formState };

      if (
        typeof finalForm.veterinarios === "string" &&
        finalForm.veterinarios.includes(",")
      ) {
        finalForm.veterinarios = finalForm.veterinarios
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
      }

      await onSubmit?.(finalForm);
    } catch (err) {
      console.error("[DynamicForm] Error en onSubmit =>", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🆕 NUEVA FUNCIÓN: Determinar texto e icono del botón según contexto
  const getSubmitButtonConfig = () => {
    // Si se pasaron props personalizados, usarlos
    if (submitButtonText && submitButtonIcon) {
      return {
        text: submitButtonText,
        icon: submitButtonIcon,
        variant: submitButtonVariant
      };
    }

    // Configuración automática según el view
    const configs = {
      activar: {
        text: 'Activar mi LemmonTag',
        icon: 'bi-check-circle',
        variant: 'primary'
      },
      ligandoSiguiente: {
        text: 'Enlazar a mi Cuenta',
        icon: 'bi-link-45deg',
        variant: 'primary'
      },
      userEdicion: {
        text: 'Guardar Cambios',
        icon: 'bi-save',
        variant: 'success'
      },
      userProfileEdit: {
        text: 'Guardar Cambios',
        icon: 'bi-save',
        variant: 'success'
      },
      lostItem: {
        text: 'Confirmar',
        icon: 'bi-check-circle',
        variant: 'warning'
      },
      adminCreacion: {
        text: 'Crear Registro',
        icon: 'bi-plus-circle',
        variant: 'primary'
      },
      adminEdicion: {
        text: 'Actualizar',
        icon: 'bi-arrow-repeat',
        variant: 'primary'
      }
    };

    return configs[view] || configs.activar;
  };

  const buttonConfig = getSubmitButtonConfig();

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <form onSubmit={handleSubmit}>
      {fields.map((field) => {
        const {
          fieldKey,
          label,
          inputType = "text",
          placeholder,
          options,
          validation,
          helperText,
        } = field;

        const validations = Array.isArray(validation) ? validation : [validation];
        const isRequired = validations.includes("required");
        const requiredMark = isRequired ? <span className="text-danger">* </span> : null;

        const value = formState[fieldKey] ?? "";
        const errorMsg = errors[fieldKey];

        // ═════════════════════════════════════════════════════
        // SECTION HEADER (visual separator, not an editable field)
        // ═════════════════════════════════════════════════════
        if (inputType === "sectionHeader") {
          return (
            <div key={fieldKey} style={{
              marginTop: 'var(--space-3)',
              marginBottom: 'var(--space-2)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              <h6 style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--gray-700)',
                marginBottom: 0
              }}>
                {label}
              </h6>
              {helperText && (
                <small style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-xs)' }}>
                  {helperText}
                </small>
              )}
            </div>
          );
        }

        // ═════════════════════════════════════════════════════
        // MODO READONLY (Formato de una línea)
        // ═════════════════════════════════════════════════════
        if (readOnly) {
          if (inputType === "checkbox") {
            return (
              <div className="mb-2" key={fieldKey}>
                <div className="d-flex">
                  <strong style={{ 
                    minWidth: "180px",
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {requiredMark}
                    {label}:
                  </strong>
                  <span className="ms-2">
                    {value ? (
                      <i className="bi bi-check-circle-fill text-success"></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-muted"></i>
                    )}
                  </span>
                </div>
              </div>
            );
          } else if (inputType === "multiselect") {
            return (
              <div className="mb-2" key={fieldKey}>
                <div className="d-flex">
                  <strong style={{ 
                    minWidth: "180px",
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {requiredMark}
                    {label}:
                  </strong>
                  <span className="ms-2" style={{fontSize: 'var(--font-size-sm)'}}>
                    {Array.isArray(value) && value.length ? value.join(", ") : "N/A"}
                  </span>
                </div>
              </div>
            );
          } else if (inputType === "file") {
            const fileName = value?.name || value || "N/A";
            return (
              <div className="mb-2" key={fieldKey}>
                <div className="d-flex">
                  <strong style={{ 
                    minWidth: "180px",
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {requiredMark}
                    {label}:
                  </strong>
                  <span className="ms-2" style={{fontSize: 'var(--font-size-sm)'}}>
                    {fileName}
                  </span>
                </div>
              </div>
            );
          } else if (inputType === "date") {
            const formattedDate = value ? new Date(value).toLocaleDateString('es-MX') : "N/A";
            return (
              <div className="mb-2" key={fieldKey}>
                <div className="d-flex">
                  <strong style={{ 
                    minWidth: "180px",
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {requiredMark}
                    {label}:
                  </strong>
                  <span className="ms-2" style={{fontSize: 'var(--font-size-sm)'}}>
                    {formattedDate}
                  </span>
                </div>
              </div>
            );
          } else {
            return (
              <div className="mb-2" key={fieldKey}>
                <div className="d-flex">
                  <strong style={{ 
                    minWidth: "180px",
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {requiredMark}
                    {label}:
                  </strong>
                  <span className="ms-2" style={{fontSize: 'var(--font-size-sm)'}}>
                    {typeof value === 'object' && value !== null ? (value.texto || "N/A") : (value || "N/A")}
                  </span>
                </div>
              </div>
            );
          }
        }
        
        // ═════════════════════════════════════════════════════
        // MODO EDICIÓN
        // ═════════════════════════════════════════════════════
        
        // CHECKBOX
        if (inputType === "checkbox") {
          let containerClasses = "form-check mb-3";
          if (fieldKey === "aceptaTerminos") {
            containerClasses += " mt-4 pt-3 border-top";
          }
          return (
            <div className={containerClasses} 
                 key={fieldKey}
                 style={{borderTopColor: 'var(--gray-300)'}}>
              <input
                className="form-check-input"
                type="checkbox"
                id={fieldKey}
                name={fieldKey}
                checked={!!value}
                onChange={(e) => handleChange(fieldKey, e.target.checked)}
              />
              <label className="form-check-label" 
                     htmlFor={fieldKey}
                     style={{fontSize: 'var(--font-size-sm)'}}>
                {requiredMark}
                {label}
              </label>
              {helperText && (
                <div className="form-text mt-1">
                  <small style={{fontSize: 'var(--font-size-xs)'}}>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="text-danger mt-1" 
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // SELECT
        if (inputType === "select") {
          return (
            <div className="mb-3" key={fieldKey}>
              <label className="form-label fw-semibold"
                     style={{
                       fontSize: 'var(--font-size-sm)',
                       fontWeight: 'var(--font-weight-semibold)'
                     }}>
                {requiredMark}
                {label}
              </label>
              <select
                className={`form-select ${errorMsg ? 'is-invalid' : ''}`}
                name={fieldKey}
                value={value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
                style={{
                  fontSize: 'var(--font-size-base)',
                  borderRadius: 'var(--radius-md)',
                  borderColor: errorMsg ? 'var(--color-danger)' : 'var(--gray-300)'
                }}
              >
                <option value="">{placeholder || "-- Seleccione --"}</option>
                {(options || []).map((opt) => {
                  const o = typeof opt === "string" ? { label: opt, value: opt } : opt;
                  return (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  );
                })}
              </select>
              {helperText && (
                <div className="form-text">
                  <small className="text-muted" 
                         style={{fontSize: 'var(--font-size-xs)'}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="invalid-feedback d-block" 
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // MULTISELECT
        if (inputType === "multiselect") {
          return (
            <div className="mb-3" key={fieldKey}>
              <label className="form-label fw-semibold"
                     style={{
                       fontSize: 'var(--font-size-sm)',
                       fontWeight: 'var(--font-weight-semibold)'
                     }}>
                {requiredMark}
                {label}
              </label>
              <div className="border rounded p-2" 
                   style={{ 
                     maxHeight: '200px', 
                     overflowY: 'auto',
                     borderColor: 'var(--gray-300)',
                     borderRadius: 'var(--radius-md)',
                     padding: 'var(--space-2)'
                   }}>
                {(options || []).map((opt) => {
                  const o = typeof opt === "string" ? { label: opt, value: opt } : opt;
                  const checked = Array.isArray(value) && value.includes(o.value ?? o.label);
                  const toggleOption = () => {
                    let newVal = Array.isArray(value) ? [...value] : [];
                    const valItem = o.value ?? o.label;
                    if (checked) {
                      newVal = newVal.filter((v) => v !== valItem);
                    } else {
                      newVal.push(valItem);
                    }
                    handleChange(fieldKey, newVal);
                  };
                  const id = `${fieldKey}_${o.value}`;
                  return (
                    <div className="form-check" key={o.value}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={id}
                        name={fieldKey}
                        checked={checked}
                        onChange={toggleOption}
                      />
                      <label className="form-check-label" 
                             htmlFor={id}
                             style={{fontSize: 'var(--font-size-sm)'}}>
                        {o.label}
                      </label>
                    </div>
                  );
                })}
              </div>
              {helperText && (
                <div className="form-text">
                  <small className="text-muted"
                         style={{fontSize: 'var(--font-size-xs)'}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="text-danger mt-1" 
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // UBICACION-SELECTOR (Estado + Ciudad con combos en cascada)
        if (inputType === "ubicacion-selector") {
          return (
            <div className="mb-3" key={fieldKey}>
              <UbicacionSelector
                estadoInicial={formState.ubicacionEstado || ""}
                ciudadInicial={formState.ubicacionCiudad || ""}
                onChange={(estado, ciudad, valida) => {
                  // Actualizar ambos campos
                  handleChange("ubicacionEstado", estado);
                  handleChange("ubicacionCiudad", ciudad);
                  
                  // Inferir país por timezone
                  const timezone = formState.timezone || "America/Mexico_City";
                  const TIMEZONE_TO_COUNTRY = {
                    'America/Mexico_City': 'MX',
                    'America/Cancun': 'MX',
                    'America/Tijuana': 'MX',
                    'America/Monterrey': 'MX',
                    'America/Chihuahua': 'MX',
                    'America/Hermosillo': 'MX',
                    'America/Merida': 'MX',
                    'America/Mazatlan': 'MX',
                  };
                  const pais = TIMEZONE_TO_COUNTRY[timezone] || 'MX';
                  handleChange("ubicacionPais", pais);
                }}
                mostrarValidacion={true}
              />
              {helperText && (
                <div className="form-text">
                  <small className="text-muted"
                         style={{fontSize: 'var(--font-size-xs)'}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="text-danger mt-1" 
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // GOOGLE PLACES AUTOCOMPLETE
        if (inputType === "google-places") {
          return (
            <GooglePlacesField
              key={fieldKey}
              fieldKey={fieldKey}
              label={label}
              placeholder={placeholder}
              helperText={helperText}
              requiredMark={requiredMark}
              errorMsg={errorMsg}
              value={value}
              onChange={handleChange}
            />
          );
        }

        // FILE
        if (inputType === "file") {
          return (
            <div className="mb-3" key={fieldKey}>
              <label className="form-label fw-semibold"
                     style={{
                       fontSize: 'var(--font-size-sm)',
                       fontWeight: 'var(--font-weight-semibold)'
                     }}>
                {requiredMark}
                {label}
              </label>
              <input
                className={`form-control ${errorMsg ? 'is-invalid' : ''}`}
                type="file"
                name={fieldKey}
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  handleChange(fieldKey, file);
                }}
                style={{
                  fontSize: 'var(--font-size-base)',
                  borderRadius: 'var(--radius-md)',
                  borderColor: errorMsg ? 'var(--color-danger)' : 'var(--gray-300)'
                }}
              />
              {helperText && (
                <div className="form-text">
                  <small className="text-muted"
                         style={{fontSize: 'var(--font-size-xs)'}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="invalid-feedback d-block"
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // DATE
        if (inputType === "date") {
          return (
            <div className="mb-3" key={fieldKey}>
              <label className="form-label fw-semibold"
                     style={{
                       fontSize: 'var(--font-size-sm)',
                       fontWeight: 'var(--font-weight-semibold)'
                     }}>
                {requiredMark}
                {label}
              </label>
              <input
                className={`form-control ${errorMsg ? 'is-invalid' : ''}`}
                type="date"
                name={fieldKey}
                value={value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
                style={{
                  fontSize: 'var(--font-size-base)',
                  borderRadius: 'var(--radius-md)',
                  borderColor: errorMsg ? 'var(--color-danger)' : 'var(--gray-300)'
                }}
              />
              {helperText && (
                <div className="form-text">
                  <small className="text-muted"
                         style={{fontSize: 'var(--font-size-xs)'}}>
                    <i className="bi bi-info-circle me-1"></i>
                    {helperText}
                  </small>
                </div>
              )}
              {errorMsg && (
                <div className="invalid-feedback d-block"
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        }

        // TEXT, EMAIL, NUMBER, etc.
        return (
          <div className="mb-3" key={fieldKey}>
            <label className="form-label fw-semibold"
                   style={{
                     fontSize: 'var(--font-size-sm)',
                     fontWeight: 'var(--font-weight-semibold)'
                   }}>
              {requiredMark}
              {label}
            </label>
            <input
              className={`form-control ${errorMsg ? 'is-invalid' : ''}`}
              type={inputType}
              name={fieldKey}
              placeholder={placeholder}
              value={value}
              onChange={(e) => handleChange(fieldKey, e.target.value)}
              onPaste={(e) => {
                if (fieldKey === 'correoConfirmacion') {
                  e.preventDefault();
                  alert('Por favor escribe tu correo manualmente para confirmar que es correcto.');
                }
              }}
              autoComplete={fieldKey === 'correoConfirmacion' ? 'off' : undefined}
              style={{
                fontSize: 'var(--font-size-base)',
                borderRadius: 'var(--radius-md)',
                borderColor: errorMsg ? 'var(--color-danger)' : 'var(--gray-300)'
              }}
            />
            {helperText && (
              <div className="form-text">
                <small className="text-muted"
                       style={{fontSize: 'var(--font-size-xs)'}}>
                  <i className="bi bi-info-circle me-1"></i>
                  {helperText}
                </small>
              </div>
            )}
            {errorMsg && (
              <div className="invalid-feedback d-block"
                   style={{fontSize: 'var(--font-size-xs)'}}>
                {errorMsg}
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <div className="d-grid gap-2 mt-4">
          <button 
            type="submit" 
            className={`btn btn-${buttonConfig.variant} btn-lg transition-base`}
            disabled={isSubmitting}
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semibold)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)'
            }}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className={`bi ${buttonConfig.icon} me-2`}></i>
                {buttonConfig.text}
              </>
            )}
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * GooglePlacesField – input con autocomplete de Google Places.
 * Siempre renderiza nuestro propio <input> para que el texto tecleado se
 * capture en el estado del formulario.  Si la Places API está disponible
 * se adjunta un Autocomplete; al seleccionar una sugerencia se emite un
 * objeto { texto, lat, lng, placeId }.  Si el usuario escribe sin
 * seleccionar, el valor se guarda como string.
 */
function GooglePlacesField({ fieldKey, label, placeholder, helperText, requiredMark, errorMsg, value, onChange }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);          // Autocomplete instance
  const onChangeRef = useRef(onChange); // stable ref – avoids stale closure
  const skipNextChangeRef = useRef(null); // text to skip in next onChange (race condition fix)

  // Keep onChange ref up to date on every render
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Display text: support both object and legacy string values
  const displayText = typeof value === 'object' && value !== null ? (value.texto || '') : (value || '');

  // Stable handler for text input – never changes identity
  const handleInput = useCallback(
    (e) => {
      const newText = e.target.value;
      // Skip if this change was triggered by place_changed setting the input
      if (skipNextChangeRef.current === newText) {
        skipNextChangeRef.current = null;
        return;
      }
      skipNextChangeRef.current = null;
      onChangeRef.current(fieldKey, newText);
    },
    [fieldKey]
  );

  // Attach Google Places Autocomplete once the API is loaded
  useEffect(() => {
    if (acRef.current) return;        // already attached
    const input = inputRef.current;
    if (!input) return;

    const tryAttach = () => {
      if (!window.google?.maps?.places) return false;

      try {
        const ac = new window.google.maps.places.Autocomplete(input, {
          types: ['geocode', 'establishment'],
          fields: ['formatted_address', 'name', 'geometry', 'place_id'],
        });

        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (place?.geometry) {
            const texto = place.formatted_address || place.name || '';
            const result = {
              texto,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              placeId: place.place_id || null,
            };
            console.log('[GooglePlaces] Place selected:', result);
            // Mark this text to skip in the next onChange (race condition: Google also fires input change)
            skipNextChangeRef.current = texto;
            onChangeRef.current(fieldKey, result);
          }
        });

        acRef.current = ac;
        console.log('[GooglePlaces] Autocomplete attached to input:', !!input);
        return true;
      } catch (err) {
        console.warn('[GooglePlaces] Failed to attach Autocomplete:', err.message);
        return false;
      }
    };

    // Try immediately; if API not yet loaded, poll every 500 ms (up to 10 s)
    if (!tryAttach()) {
      const interval = setInterval(() => {
        if (tryAttach()) clearInterval(interval);
      }, 500);
      const timeout = setTimeout(() => clearInterval(interval), 10000);
      return () => { clearInterval(interval); clearTimeout(timeout); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold"
             style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}>
        {requiredMark}
        {label}
      </label>
      <input
        ref={inputRef}
        className={`form-control ${errorMsg ? 'is-invalid' : ''}`}
        type="text"
        name={fieldKey}
        placeholder={placeholder}
        value={displayText}
        onChange={handleInput}
        style={{
          fontSize: 'var(--font-size-base)',
          borderRadius: 'var(--radius-md)',
          borderColor: errorMsg ? 'var(--color-danger)' : 'var(--gray-300)',
        }}
      />
      {helperText && (
        <div className="form-text">
          <small className="text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
            <i className="bi bi-info-circle me-1"></i>
            {helperText}
          </small>
        </div>
      )}
      {errorMsg && (
        <div className="invalid-feedback d-block" style={{ fontSize: 'var(--font-size-xs)' }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
}

export default DynamicForm;